import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ERROR_CODES } from '@tronche2tek/shared-types';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/errors';
import type { PendingPhoto } from '../../api/pending';
import {
  approvePending,
  cancelOwnPending,
  pendingCountQueryKey,
  pendingQueryKey,
  pendingQueryOptions,
  rejectPending,
} from '../../api/pending';
import type { GraphUser } from '../../api/users';
import {
  deleteUserPhoto,
  uploadUserPhoto,
  userPhotoQueryKey,
  userPhotoQueryOptions,
} from '../../api/users';
import { graphErrorMessage } from '../../utils/graph-error';
import { RejectDialog } from '../admin/RejectDialog';
import { Card } from '../common/Card';
import { PhotoBoard } from './PhotoBoard';

interface PhotoManagerProps {
  num: number;
  oid: string;
  user: GraphUser;
  isSelf: boolean;
  /**
   * OID of the reader. A pending photo they submitted themselves cannot be
   * approved by them (four-eyes, enforced server-side) — the board needs to
   * know to disable the button rather than offer a 403.
   */
  actorOid: string | null;
  /**
   * `moderate:Photo` — arbitrate somebody else's submission.
   */
  canModerate: boolean;
  /**
   * `delete:Photo`. Role `user` holds `read:Photo` and `upload:Photo` and not
   * this one, while `DELETE /users/:userId/photo` requires it — so without the
   * gate every plain user with a photo was offered a button whose only outcome
   * is a 403. Read on the page beside `canModerate` rather than from
   * `AbilityContext` here: `$oid.tsx` already derives four such facts from the
   * ability and passes them down, and keeping this one with them means the
   * card takes authorisation as data, which is also what lets it be tested
   * without a provider.
   */
  canDelete: boolean;
  /**
   * Le lecteur est admin : sur sa propre fiche, sa photo atteint Entra
   * directement. Pour tous les autres — opérateur compris, qui est lui-même
   * salarié — envoyer sa photo crée une demande qu'un tiers approuve.
   */
  isAdmin: boolean;
}

// One status channel, not five copies of the same branch. The server's raw
// exception message is not fit to show here — every one of these routes
// sends unmodified English prose, sometimes with a GUID in it (see
// users.controller.ts, users.service.ts, photo-moderation.service.ts) — so
// this maps the status instead of the text. No route sends a machine
// `code` either (that only exists for auth denials), so status is what
// there is. Falls back to the unclassified generic string otherwise.
function describeFailure(t: TFunction, err: unknown): string {
  if (err instanceof ApiError) {
    // Microsoft a refusé et a écrit pourquoi, en une phrase faite pour être
    // lue. La rendre telle quelle bat n'importe quel générique traduit : les
    // statuts sont partagés par des refus sans rapport, et cette prose est
    // la seule qui nomme la cause. Le `request-id` est ce que le support
    // Microsoft demande en premier.
    if (err.requestId) {
      return t('photo.status.error.upstream', {
        message: graphErrorMessage(t, err.code) ?? err.message,
        requestId: err.requestId,
      });
    }
    if (err.status === 400) {
      return t('photo.status.error.badRequest');
    }
    // Deux 403 différents sur la même route : « pas le droit sur cette
    // fiche » et « cette personne est un salarié, seul un admin efface sa
    // photo ». Le second est le seul refus qu'un opérateur peut rencontrer
    // sur une fiche qu'il a par ailleurs le droit de gérer, et le message
    // générique le laissait croire à un bug de droits — d'où le code
    // machine, que le serveur pose exprès pour être distingué ici.
    if (err.code === API_ERROR_CODES.EMPLOYEE_DELETE_ADMIN_ONLY) {
      return t('photo.status.error.employeeDeleteAdminOnly');
    }
    if (err.status === 403) {
      return t('photo.status.error.forbidden');
    }
    if (err.status === 404) {
      return t('photo.status.error.notFound');
    }
    if (err.status === 413) {
      return t('photo.status.error.tooLarge');
    }
    if (err.status >= 500) {
      return t('photo.status.error.server');
    }
  }
  return t('photo.status.failed');
}

/** Who filed the pending row, as the board needs to know it. */
function attribution(
  pendingRow: PendingPhoto | null,
  actorOid: string | null,
  oid: string,
): { ownSubmission: boolean; submitterLabel: string | null } {
  // Four-eyes: the reader submitted this photo, so the server will refuse
  // their approval — the board disables the button instead of offering a 403.
  const ownSubmission =
    !!pendingRow?.submittedByOid &&
    !!actorOid &&
    pendingRow.submittedByOid === actorOid;

  // Who filed it, shown to moderators when it was not the subject themselves
  // (a self-submission's submitter is the subject — restating it says nothing).
  const submitterLabel =
    pendingRow?.submittedByOid && pendingRow.submittedByOid !== oid
      ? (pendingRow.submittedByName ?? pendingRow.submittedByUpn ?? null)
      : null;

  return { ownSubmission, submitterLabel };
}

function DeleteConfirm({
  displayName,
  removing,
  onCancel,
  onConfirm,
}: {
  displayName: string;
  removing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="@container mt-5">
      <p className="mb-4 border-l-2 border-l-coral pl-3 text-base">
        {t('photoViewer.removeConfirm', { name: displayName })}
      </p>
      <div className="plate-strip">
        <button type="button" className="plate-act" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="plate-act plate-act-destroy"
          disabled={removing}
          onClick={onConfirm}
        >
          {removing ? t('photoViewer.removing') : t('photoViewer.remove')}
        </button>
      </div>
    </div>
  );
}

/**
 * One card for one subject: the photo this person has, the one proposed to
 * replace it, and the ways to act on either.
 *
 * This was four components — a viewer, an upload form, a moderation strip, and
 * a fourth that passed eight props between them. Three seams ran through them:
 * the current photo was fetched twice, two data idioms coexisted (raw fetch over
 * useApi, and react-query) which is why a four-part refresh chain existed where
 * a query key would do, and three idioms reported success and failure on the
 * same object.
 *
 * Everything that touches the network is here. PhotoBoard takes props and
 * emits callbacks, which is what makes it testable without a mock.
 */
export function PhotoManager({
  num,
  oid,
  user,
  isSelf,
  actorOid,
  canModerate,
  canDelete,
  isAdmin,
}: PhotoManagerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  // Bumped on every successful upload, and used as the board's `key`: a new
  // key is a new element, so React unmounts the old one — staged crop, object
  // URLs and all — instead of leaving an armed Envoyer beside "Photo mise à
  // jour.", which a second click would have sent again.
  const [uploadCount, setUploadCount] = useState(0);

  const {
    data: currentBlob,
    isError: currentFailed,
    isLoading: currentLoading,
  } = useQuery(userPhotoQueryOptions(oid));
  const { data: pending } = useQuery({
    ...pendingQueryOptions(oid),
    enabled: isSelf || canModerate,
  });

  const targetParams = {
    targetUpn: user.userPrincipalName,
    targetName: user.displayName,
  };

  function invalidatePending() {
    void queryClient.invalidateQueries({ queryKey: pendingQueryKey(oid) });
    void queryClient.invalidateQueries({ queryKey: pendingCountQueryKey });
  }

  function invalidatePhoto() {
    void queryClient.invalidateQueries({ queryKey: userPhotoQueryKey(oid) });
  }

  // Every mutation clears the status as it starts. Without this the previous
  // outcome sat under the card indefinitely — the toasts it replaced dismissed
  // themselves — and, worse, described the wrong attempt for the up-to-six
  // seconds an upload spends retrying.
  const clearStatus = () => setStatus(null);

  const upload = useMutation({
    mutationFn: (blob: Blob) => uploadUserPhoto(oid, blob, targetParams),
    // Replaces PhotoUpload's hand-rolled loop: two retries, linear backoff.
    // The old loop caught any thrown error — a transport failure included —
    // and retried it, refusing only a definite 4xx because that will not
    // succeed on retry. A non-ApiError (network blip, DNS hiccup) gets the
    // same benefit of the doubt here; only a real ApiError below 500 is
    // refused.
    retry: (count, err) =>
      count < 2 && (!(err instanceof ApiError) || err.status >= 500),
    retryDelay: (count) => (count + 1) * 2000,
    onMutate: clearStatus,
    onSuccess: (ack) => {
      // A self-upload by somebody without moderate:Photo does not reach Entra;
      // the server files it for moderation, and the ack is how we know which
      // happened. Both keys change either way, and so does the moderation
      // badge in the top bar — which nothing invalidated before.
      invalidatePhoto();
      invalidatePending();
      setUploadCount((c) => c + 1);
      // Two ways to land in the queue: one's own photo awaiting a moderator,
      // or an operator's upload for a Lucca employee — which a DIFFERENT
      // operator must approve, and the message says so.
      const filed = isSelf
        ? t('photo.status.filed')
        : t('photo.status.filedForApproval');
      setStatus('status' in ack ? filed : t('photo.status.uploaded'));
    },
    onError: (err) => setStatus(describeFailure(t, err)),
  });

  const remove = useMutation({
    mutationFn: () => deleteUserPhoto(oid, targetParams),
    onMutate: clearStatus,
    onSuccess: () => {
      invalidatePhoto();
      setConfirmDelete(false);
      setStatus(t('photo.status.deleted'));
    },
    onError: (err) => setStatus(describeFailure(t, err)),
  });

  const approve = useMutation({
    mutationFn: () => {
      // Only reachable from the review card, which renders when one exists.
      if (!pending) {
        throw new Error('approve without a pending photo');
      }
      return approvePending(pending.id);
    },
    onMutate: clearStatus,
    onSuccess: () => {
      invalidatePending();
      // The approved photo is now the Entra one: drop the cached avatar so it
      // refetches everywhere, not only where this card is mounted.
      invalidatePhoto();
      setStatus(t('photo.status.approved'));
    },
    onError: (err) => setStatus(describeFailure(t, err)),
  });

  const reject = useMutation({
    mutationFn: (reason?: string) => {
      if (!pending) {
        throw new Error('reject without a pending photo');
      }
      return rejectPending(pending.id, reason);
    },
    onMutate: clearStatus,
    onSuccess: () => {
      invalidatePending();
      setStatus(t('photo.status.rejected'));
    },
    onError: (err) => setStatus(describeFailure(t, err)),
  });

  const cancel = useMutation({
    mutationFn: cancelOwnPending,
    onMutate: clearStatus,
    onSuccess: () => {
      invalidatePending();
      setStatus(t('photo.status.cancelled'));
    },
    onError: (err) => setStatus(describeFailure(t, err)),
  });

  const busy =
    upload.isPending ||
    remove.isPending ||
    approve.isPending ||
    reject.isPending ||
    cancel.isPending;

  const pendingRow = pending ?? null;
  const { ownSubmission, submitterLabel } = attribution(
    pendingRow,
    actorOid,
    oid,
  );

  return (
    <Card
      num={num}
      accent="blue"
      title={t('users.detail.photo.title')}
      meta={t('users.detail.photo.meta')}
    >
      <PhotoBoard
        key={uploadCount}
        currentBlob={currentBlob ?? null}
        currentFailed={currentFailed}
        currentLoading={currentLoading}
        displayName={user.displayName}
        pending={pendingRow}
        isSelf={isSelf}
        canModerate={canModerate}
        canDelete={canDelete}
        selfUploadFilesRequest={!isAdmin}
        ownSubmission={ownSubmission}
        submitterLabel={submitterLabel}
        busy={busy}
        onSubmit={(blob) => upload.mutate(blob)}
        onRefresh={invalidatePhoto}
        onDelete={() => setConfirmDelete(true)}
        onApprove={() => approve.mutate()}
        onReject={() => setRejectOpen(true)}
        onCancel={() => cancel.mutate()}
      />

      {/* Mounted from the first render, empty until there is something to
          say: a live region inserted at the same moment as its text is
          announced unreliably, because the assistive technology has to be
          watching the container before the text lands in it. */}
      <div role="status" aria-live="polite">
        {status && <p className="mt-4 text-sm">{status}</p>}
      </div>

      {/* The question marked in coral like the rejection reason it resembles,
          then the same two-cell strip the rest of the card uses. The cell that
          destroys is coral-filled: green is this app's "yes, send it", and it
          was carrying the deletion. */}
      {confirmDelete && canDelete && (
        <DeleteConfirm
          displayName={user.displayName}
          removing={remove.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => remove.mutate()}
        />
      )}

      <RejectDialog
        // pendingRow can go null while this is open — another moderator acted,
        // or the subject withdrew — and reject.mutationFn reads `pending!.id`.
        // Closing here rather than letting a confirm click reach that
        // assertion is one line against a throw the mutation would otherwise
        // report as a generic failure.
        open={rejectOpen && pendingRow !== null}
        submitterName={user.displayName}
        onConfirm={(reason) => {
          reject.mutate(reason);
          setRejectOpen(false);
        }}
        onCancel={() => setRejectOpen(false)}
      />
    </Card>
  );
}
